import { Moon, Monitor, Sun } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';

const options = [
  { value: 'light', icon: Sun, label: 'Clair' },
  { value: 'system', icon: Monitor, label: 'Systeme' },
  { value: 'dark', icon: Moon, label: 'Sombre' },
] as const;

interface ThemeToggleProps {
  size?: 'sm' | 'md';
  variant?: 'segmented' | 'icon';
  className?: string;
}

export function ThemeToggle({ size = 'sm', variant = 'segmented', className }: ThemeToggleProps) {
  const { theme, setTheme, resolvedTheme } = useTheme();

  if (variant === 'icon') {
    const isDark = resolvedTheme === 'dark';
    const btnSize = size === 'sm' ? 'h-8 w-8' : 'h-9 w-9';
    const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-4.5 w-4.5';
    const toggleTheme = () => setTheme(isDark ? 'light' : 'dark');
    const label = isDark ? 'Passer en mode clair' : 'Passer en mode sombre';

    return (
      <button
        type="button"
        onClick={toggleTheme}
        className={cn(
          'inline-flex items-center justify-center rounded-md border text-charcoal-700 dark:text-charcoal-200 transition-colors',
          'bg-neutral-100 border-neutral-200 hover:bg-neutral-200',
          'dark:bg-charcoal-800 dark:border-charcoal-700 dark:hover:bg-charcoal-700',
          btnSize,
          className,
        )}
        aria-label={label}
        title={label}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={isDark ? 'dark' : 'light'}
            initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
            animate={{ rotate: 0, opacity: 1, scale: 1 }}
            exit={{ rotate: 90, opacity: 0, scale: 0.6 }}
            transition={{ duration: 0.15 }}
          >
            {isDark ? <Sun className={iconSize} /> : <Moon className={iconSize} />}
          </motion.div>
        </AnimatePresence>
      </button>
    );
  }

  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const btnSize = size === 'sm' ? 'h-8 w-8' : 'h-9 w-9';

  return (
    <div
      className={cn(
        'inline-flex rounded-lg p-0.5 border',
        'bg-neutral-100 border-neutral-200',
        'dark:bg-charcoal-800 dark:border-charcoal-700',
        className,
      )}
      role="radiogroup"
      aria-label="Theme"
    >
      {options.map(({ value, icon: Icon, label }) => {
        const isActive = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => setTheme(value)}
            className={cn('relative flex items-center justify-center rounded-md', btnSize)}
            aria-label={`Theme ${label}`}
            title={label}
          >
            {isActive && (
              <motion.div
                layoutId="theme-indicator"
                className={cn(
                  'absolute inset-0 rounded-md shadow-sm',
                  'bg-white dark:bg-charcoal-600',
                  'ring-1 ring-lime-500/30',
                )}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <Icon
              className={cn(
                'relative z-10',
                iconSize,
                isActive
                  ? 'text-lime-700 dark:text-lime-400'
                  : 'text-charcoal-400 dark:text-charcoal-500',
              )}
            />
          </button>
        );
      })}
    </div>
  );
}
